/** @jsxImportSource @emotion/react */
import { css, SerializedStyles } from "@emotion/react";

import { ReactNode, useRef } from "react";

import { colors } from "@dotnet/telematics-design-system";

import { CompaniesHierarchyDataDto } from "../../../shared";
import { useIsXOverflow } from "../../../shared/hooks/useIsXOverflow";
import { LongTextTooltip } from "../../Tooltip/LongTextTooltip";
import CompanyHierarchyTreeLines from "../components/DesktopTable/components/CompanyHierarchyTreeLines/CompanyHierarchyTreeLines";
import { staticTableStyles } from "../styles/styles";

const { tableCheckboxDiv, companyTitleDivInDesktopCell } = staticTableStyles;

const boxShadowBottom = `inset 0px -1px 0px ${colors.gray200}`;
const checkedLeftBorder = `inset 2px 0px 0px ${colors.blue700}`;

// Builds each distinct style once and reuses it (there are only a few variants)
const memoizeStyle = <Args extends (string | number | boolean)[]>(
  buildStyle: (...args: Args) => SerializedStyles
) => {
  const styleCache = new Map<string, SerializedStyles>();

  return (...args: Args) => {
    const cacheKey = args.join("|");
    let style = styleCache.get(cacheKey);

    if (!style) {
      style = buildStyle(...args);
      styleCache.set(cacheKey, style);
    }

    return style;
  };
};

const getIndentCellStyle = memoizeStyle(
  (isFirstColumn: boolean, hasBottomShadow: boolean) =>
    css({
      width: isFirstColumn ? `50px` : `40px`,
      backgroundColor: `${colors.gray50} !important`,
      boxShadow: hasBottomShadow ? boxShadowBottom : "unset"
    })
);

const getCheckboxDivStyle = memoizeStyle(
  (isChecked: boolean, hasLeftBorder: boolean) =>
    css([
      tableCheckboxDiv,
      {
        padding: "10px 8px",
        boxShadow: isChecked
          ? `${checkedLeftBorder}, ${boxShadowBottom}`
          : `${boxShadowBottom}`,
        borderLeft: hasLeftBorder
          ? `1px solid ${colors.gray200} !important`
          : "unset",
        alignItems: "center",
        display: "flex"
      }
    ])
);

const companyNameStyle = css([
  companyTitleDivInDesktopCell,
  {
    paddingLeft: "12px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    width: 0,
    flex: "1 1 0",
    minWidth: 0
  }
]);

const useCompaniesCheckboxFunctions = (
  row: CompaniesHierarchyDataDto,
  companyLevelsList: number[],
  checkedItem: boolean,
  index?: number
) => {
  const companyNameRef = useRef<HTMLDivElement>(null);
  const isOverflowing = useIsXOverflow(companyNameRef);

  const hasLeftBorder = Boolean(row?.level && row.level > 0);

  const generateEmptyCellCountOfIndexNumber = (
    boxShadow: boolean,
    indexOfxAxisEmptyDiv: number,
    xAxisEmptyDivsArray: unknown[]
  ) => {
    return (
      <div
        // Stable key: the old uuidv7() produced a new key every render, which
        // unmounted/remounted every indent div and tree-lines component each time.
        key={indexOfxAxisEmptyDiv}
        css={getIndentCellStyle(indexOfxAxisEmptyDiv === 0, boxShadow)}
      >
        <CompanyHierarchyTreeLines
          indexOfxAxisEmptyDiv={indexOfxAxisEmptyDiv}
          companyLevelsList={companyLevelsList}
          tableRowIndex={index}
          xAxisEmptyDivsCount={xAxisEmptyDivsArray.length}
        />
      </div>
    );
  };

  const generateIndentedCheckboxDivWithCompanyName = (children: ReactNode) => {
    return (
      <LongTextTooltip title={row?.name} open={isOverflowing}>
        <div css={getCheckboxDivStyle(checkedItem, hasLeftBorder)}>
          {children}
          <div ref={companyNameRef} css={companyNameStyle}>
            {row.name}
          </div>
        </div>
      </LongTextTooltip>
    );
  };

  const mapThroughCompaniesLevels = (
    indexOfEmptyXAxisDiv: number,
    arrayOfRowLevels: number[]
  ): boolean => {
    if (index === undefined) return false;

    const currentLevel = companyLevelsList[index];
    const nextLevel = companyLevelsList[index + 1];

    if (currentLevel > nextLevel) {
      const gap = nextLevel - currentLevel;
      const absGap = Math.abs(gap);

      const lastDivWithoutShadow = arrayOfRowLevels.length - 1 - absGap;

      return (
        (gap <= -2 && indexOfEmptyXAxisDiv > lastDivWithoutShadow) ||
        (gap === -1 && indexOfEmptyXAxisDiv > lastDivWithoutShadow) ||
        (gap === -1 && nextLevel === 1)
      );
    }

    return false;
  };

  return {
    generateEmptyCellCountOfIndexNumber,
    generateIndentedCheckboxDivWithCompanyName,
    mapThroughCompaniesLevels
  };
};

export default useCompaniesCheckboxFunctions;
